import type {
  CoachRuntimeReadModel,
  CoachRuntimeServiceReadModel,
  CoachServiceReplacementOperatorResult,
  CoachServiceReplacementPreviewReadModel,
} from "@billiard/contracts";
import { ArrowRightLeft, Check, Clock3 } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  type CoachServiceReplacementCommand,
  coachServiceReplacementFailureMessage,
  coachServiceReplacementOutcomeIsUnknown,
  getCoachServiceReplacementByRequest,
  previewCoachServiceReplacement,
  replaceCoachService,
} from "./api";
import {
  beginCoachServiceReplacementDispatch,
  type CoachServiceReplacementRecovery,
  type CoachServiceReplacementRecoveryView,
  claimCoachServiceReplacementRecovery,
  clearCoachServiceReplacementRecovery,
  coachServiceReplacementContext,
  finishCoachServiceReplacementDispatch,
  markCoachServiceReplacementOutcomeUnknown,
  readCoachServiceReplacementRecovery,
  reconcileCoachServiceReplacementAuthority,
} from "./coachServiceReplacementRecovery";

function nextReplacementKey(): string {
  const random = globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random()}`;
  return `coach-service-replacement:${random}`;
}

function money(fen: number): string {
  return `¥${(fen / 100).toFixed(2)}`;
}

function dateTime(value: string): string {
  return new Intl.DateTimeFormat("zh-CN", {
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).format(new Date(value));
}

export function CoachServiceReplacementPanel({
  operator,
  selectedService,
  replacementCoaches,
  showCommissionAmount,
  blocked,
  onRecoveryViewChange,
  onCompleted,
}: {
  operator: { tenantId: string; storeId: string; id: string };
  selectedService: CoachRuntimeServiceReadModel | undefined;
  replacementCoaches: CoachRuntimeReadModel["profiles"];
  showCommissionAmount: boolean;
  blocked: boolean;
  onRecoveryViewChange?: (view: CoachServiceReplacementRecoveryView) => void;
  onCompleted: () => Promise<void> | void;
}): React.JSX.Element {
  const context = useMemo(() => coachServiceReplacementContext(operator), [operator]);
  const [replacementCoachId, setReplacementCoachId] = useState("");
  const [reason, setReason] = useState("");
  const [pending, setPending] = useState(false);
  const [preview, setPreview] = useState<CoachServiceReplacementPreviewReadModel | null>(null);
  const [message, setMessage] = useState<{ kind: "INFO" | "ERROR"; text: string } | null>(null);
  const [completed, setCompleted] = useState<CoachServiceReplacementOperatorResult | null>(null);
  const [recoveryView, setRecoveryViewState] = useState<CoachServiceReplacementRecoveryView>(() =>
    readCoachServiceReplacementRecovery(context),
  );
  const readingRef = useRef<string | null>(null);

  const setRecoveryView = useCallback(
    (view: CoachServiceReplacementRecoveryView): void => {
      setRecoveryViewState(view);
      onRecoveryViewChange?.(view);
    },
    [onRecoveryViewChange],
  );

  const recoverAuthority = useCallback(
    async (recovery: CoachServiceReplacementRecovery): Promise<void> => {
      if (recovery.state !== "OUTCOME_UNKNOWN") return;
      const requestKey = recovery.command.request.idempotencyKey;
      if (readingRef.current === requestKey) return;
      readingRef.current = requestKey;
      setMessage({
        kind: "INFO",
        text: "正在用原请求号核对门店结果。确认前不能再次换人，也不能改动助教服务。",
      });
      try {
        const result = await getCoachServiceReplacementByRequest(recovery.command);
        const resolution = reconcileCoachServiceReplacementAuthority(recovery, context, result);
        setRecoveryView(readCoachServiceReplacementRecovery(context));
        if (resolution.status === "EXECUTED") {
          setCompleted(resolution.result);
          setMessage({ kind: "INFO", text: "门店已确认原换人完成，不会重复结束或重复上钟。" });
          await onCompleted();
        } else if (resolution.status === "RETRY_ALLOWED") {
          setMessage({
            kind: "INFO",
            text: "门店已明确原换人没有执行。只能使用已保存的原请求重试，不能修改内容或换请求号。",
          });
        } else {
          setMessage({
            kind: "ERROR",
            text: "门店结果无法与本机保存的原服务对应。换人继续锁定，请重新读取后由店长核对。",
          });
        }
      } catch (error) {
        setMessage({
          kind: "ERROR",
          text: `${coachServiceReplacementFailureMessage(error)} 相关服务继续锁定，请勿重新提交。`,
        });
      } finally {
        readingRef.current = null;
      }
    },
    [context, onCompleted, setRecoveryView],
  );

  useEffect(() => {
    const current = readCoachServiceReplacementRecovery(context);
    setRecoveryView(current);
    if (current.status === "OWNED" && current.recovery.state === "OUTCOME_UNKNOWN") {
      void recoverAuthority(current.recovery);
    } else if (current.status === "CONTEXT_MISMATCH") {
      setMessage({
        kind: "ERROR",
        text: "本机保存着另一名员工的助教换人原请求。当前账号不能接管或重试，请切回原账号核对。",
      });
    } else if (current.status === "STORAGE_UNAVAILABLE") {
      setMessage({
        kind: "ERROR",
        text: "浏览器无法安全保存或读取换人保护记录。为防止重复计费和提成，助教经营操作已暂停。",
      });
    }
  }, [context, recoverAuthority, setRecoveryView]);

  useEffect(() => {
    if (
      replacementCoachId &&
      !replacementCoaches.some((coach) => coach.id === replacementCoachId)
    ) {
      setReplacementCoachId("");
    }
  }, [replacementCoachId, replacementCoaches]);

  useEffect(() => {
    if (
      preview &&
      (preview.serviceId !== selectedService?.id ||
        preview.expectedServiceVersion !== selectedService?.version)
    ) {
      setPreview(null);
    }
  }, [preview, selectedService?.id, selectedService?.version]);

  const dispatch = async (recovery: CoachServiceReplacementRecovery): Promise<void> => {
    if (!beginCoachServiceReplacementDispatch(recovery, context)) {
      setMessage({ kind: "ERROR", text: "原换人请求当前不能安全提交，页面没有发出新请求。" });
      return;
    }
    setPending(true);
    setMessage({ kind: "INFO", text: "正在一次完成原助教截止和接替助教上钟，请勿重复点击。" });
    try {
      const result = await replaceCoachService(recovery.command);
      if (!clearCoachServiceReplacementRecovery(recovery, context)) {
        setMessage({
          kind: "ERROR",
          text: "门店已返回成功，但浏览器无法清除保护记录。助教经营操作继续暂停，请勿重试。",
        });
        return;
      }
      setRecoveryView(readCoachServiceReplacementRecovery(context));
      setCompleted(result);
      setMessage({ kind: "INFO", text: "助教换人已一次完成，两段服务、费用和提成都已分别保存。" });
      setReplacementCoachId("");
      setReason("");
      setPreview(null);
      await onCompleted();
    } catch (error) {
      if (coachServiceReplacementOutcomeIsUnknown(error)) {
        markCoachServiceReplacementOutcomeUnknown(recovery, context);
        const current = readCoachServiceReplacementRecovery(context);
        setRecoveryView(current);
        setMessage({
          kind: "INFO",
          text: "换人结果未知。系统会先查询原请求，结果明确前不会允许再次换人或改动服务。",
        });
        if (current.status === "OWNED") await recoverAuthority(current.recovery);
      } else {
        clearCoachServiceReplacementRecovery(recovery, context);
        setRecoveryView(readCoachServiceReplacementRecovery(context));
        setPreview(null);
        setMessage({ kind: "ERROR", text: coachServiceReplacementFailureMessage(error) });
      }
    } finally {
      finishCoachServiceReplacementDispatch(recovery, context);
      setPending(false);
    }
  };

  const requestPreview = async (): Promise<void> => {
    if (!selectedService || selectedService.status !== "ACTIVE") {
      setMessage({
        kind: "ERROR",
        text: "请选择状态为“服务中”的任务；待上钟或已结束服务不能换人。",
      });
      return;
    }
    const replacementCoach = replacementCoaches.find((coach) => coach.id === replacementCoachId);
    if (!replacementCoach || replacementCoach.id === selectedService.coachId) {
      setMessage({ kind: "ERROR", text: "请选择另一名当前可接单的助教。" });
      return;
    }
    const normalizedReason = reason.trim();
    if ([...normalizedReason].length < 2 || [...normalizedReason].length > 500) {
      setMessage({
        kind: "ERROR",
        text: "换人原因必须填写 2 至 500 个字，说明顾客或现场发生了什么。",
      });
      return;
    }
    setPending(true);
    setMessage({ kind: "INFO", text: "正在按当前服务时长核算原助教费用、提成和接替价格。" });
    try {
      const value = await previewCoachServiceReplacement(selectedService, {
        replacementCoachId: replacementCoach.id,
        expectedServiceVersion: selectedService.version,
        reason: normalizedReason,
      });
      setPreview(value);
      setCompleted(null);
      setMessage({
        kind: "INFO",
        text: "金额和接替价格已冻结 5 分钟。请核对下面结果后再确认换人。",
      });
    } catch (error) {
      setPreview(null);
      setMessage({ kind: "ERROR", text: coachServiceReplacementFailureMessage(error) });
    } finally {
      setPending(false);
    }
  };

  const confirmReplacement = (): void => {
    if (
      !selectedService ||
      !preview ||
      preview.serviceId !== selectedService.id ||
      preview.expectedServiceVersion !== selectedService.version ||
      preview.replacementCoachId !== replacementCoachId ||
      preview.reason !== reason.trim()
    ) {
      setPreview(null);
      setMessage({ kind: "ERROR", text: "当前选择已与金额预览不同，请重新生成预览后再确认。" });
      return;
    }
    const command: CoachServiceReplacementCommand = {
      context,
      baseline: selectedService,
      request: {
        replacementCoachId: preview.replacementCoachId,
        expectedServiceVersion: preview.expectedServiceVersion,
        reason: preview.reason,
        effectiveAt: preview.effectiveAt,
        previewExpiresAt: preview.expiresAt,
        expectedEffectiveSeconds: preview.expectedEffectiveSeconds,
        expectedBilledMinutes: preview.expectedBilledMinutes,
        expectedServiceFeeFen: preview.expectedServiceFeeFen,
        expectedCommissionBaseFen: preview.expectedCommissionBaseFen,
        expectedCommissionFen: preview.expectedCommissionFen,
        replacementRuleId: preview.replacementRuleId,
        replacementRuleVersion: preview.replacementRuleVersion,
        replacementPriceFen: preview.replacementPriceFen,
        replacementPricingToken: preview.replacementPricingToken,
        replacementToken: preview.replacementToken,
        idempotencyKey: nextReplacementKey(),
      },
    };
    const claimed = claimCoachServiceReplacementRecovery(context, command);
    setRecoveryView(claimed);
    if (claimed.status !== "OWNED") {
      setMessage({
        kind: "ERROR",
        text: "无法安全保存这次换人的原请求，系统没有执行。请先处理页面显示的待确认请求。",
      });
      return;
    }
    void dispatch(claimed.recovery);
  };

  const active = selectedService?.status === "ACTIVE";
  const locked = recoveryView.status !== "NONE";

  return (
    <section className="coach-replacement-panel" aria-label="助教原子换人">
      <div className="coach-section-title compact">
        <div>
          <span>服务不中断</span>
          <h3>更换服务中的助教</h3>
        </div>
        <ArrowRightLeft size={20} aria-hidden="true" />
      </div>
      <p className="coach-replacement-consequence">
        确认后会在同一次操作中截止原助教服务并结算其分钟、费用和提成；接替助教会立即上钟并另建服务记录。不会先结束再重新分配。
      </p>

      {active ? (
        <div className="coach-replacement-form">
          <p>
            当前任务：{selectedService.tableNumber} 号桌 · 版本 {selectedService.version} · 已于{" "}
            {selectedService.startedAt ? dateTime(selectedService.startedAt) : "未上钟"} 开始 ·
            原服务归属营业日 {selectedService.businessDate}
          </p>
          <label>
            接替助教
            <select
              value={replacementCoachId}
              disabled={pending || locked || blocked}
              onChange={(event) => {
                setReplacementCoachId(event.target.value);
                setPreview(null);
                setCompleted(null);
              }}
            >
              <option value="">请选择另一名可接单助教</option>
              {replacementCoaches.map((coach) => (
                <option value={coach.id} key={coach.id}>
                  {coach.coachNumber} · {coach.displayName}
                </option>
              ))}
            </select>
            <small>这里只显示已签到且没有其他进行中服务的助教；选中后会立即接替上钟。</small>
          </label>
          <label>
            换人原因
            <textarea
              value={reason}
              maxLength={500}
              disabled={pending || locked || blocked}
              placeholder="例如：顾客要求更换助教，已现场确认"
              onChange={(event) => {
                setReason(event.target.value);
                setPreview(null);
                setCompleted(null);
              }}
            />
            <small>必须填写 2 至 500 个字；原因会保存在原服务截止记录中。</small>
          </label>
          {preview ? (
            <output className="coach-replacement-preview">
              <strong>请核对本次换人结果</strong>
              <span>
                原助教服务截止到 {dateTime(preview.effectiveAt)}，共计{" "}
                {preview.expectedBilledMinutes} 分钟，费用 {money(preview.expectedServiceFeeFen)}
                {showCommissionAmount ? `，预计提成 ${money(preview.expectedCommissionFen)}` : ""}。
              </span>
              <span>
                接替助教从同一时点开始“{preview.replacementServiceTypeName}”服务，当前价格{" "}
                {money(preview.replacementPriceFen)}。确认后两段记录会在同一次操作中保存。
              </span>
              <small>
                该预览有效到 {dateTime(preview.expiresAt)}；超时或服务事实变化时不会执行。
              </small>
              <button
                className="button primary"
                type="button"
                disabled={pending || locked || blocked}
                onClick={confirmReplacement}
              >
                <ArrowRightLeft size={17} aria-hidden="true" /> 确认按此金额换人
              </button>
            </output>
          ) : (
            <button
              className="button primary"
              type="button"
              disabled={pending || locked || blocked || replacementCoaches.length === 0}
              onClick={() => void requestPreview()}
            >
              <ArrowRightLeft size={17} aria-hidden="true" /> 先核对金额和接替价格
            </button>
          )}
          {replacementCoaches.length === 0 ? (
            <p className="disabled-reason">
              当前没有另一名可接单助教。请让助教先签到并恢复接单，或等待其他服务结束后再试。
            </p>
          ) : null}
        </div>
      ) : (
        <p className="disabled-reason">请先在“当前任务”选择一条状态为“服务中”的记录。</p>
      )}

      {recoveryView.status === "OWNED" ? (
        <div className="coach-replacement-recovery" role="alert">
          <strong>原换人请求待确认</strong>
          <span>原助教、接替助教、版本、原因和请求号已锁定，不能改成另一笔。</span>
          {recoveryView.canRetry ? (
            <button
              className="button secondary"
              type="button"
              disabled={pending}
              onClick={() => void dispatch(recoveryView.recovery)}
            >
              使用原请求安全重试
            </button>
          ) : null}
        </div>
      ) : recoveryView.status !== "NONE" ? (
        <div className="coach-replacement-recovery" role="alert">
          当前账号或浏览器不能安全处理已保存的原换人请求，助教经营操作已暂停。
        </div>
      ) : null}

      {message ? (
        <output className={message.kind === "ERROR" ? "error-band" : "coach-form-note"}>
          <Clock3 size={17} aria-hidden="true" />
          <span>{message.text}</span>
        </output>
      ) : null}

      {completed ? (
        <output className="coach-replacement-result" aria-live="polite">
          <div>
            <Check size={18} aria-hidden="true" />
            <strong>换人已完成</strong>
          </div>
          <dl>
            <div>
              <dt>原服务截止时间</dt>
              <dd>{dateTime(completed.previousService.endedAt)}</dd>
            </div>
            <div>
              <dt>原服务计费分钟</dt>
              <dd>{completed.previousService.billedMinutes} 分钟</dd>
            </div>
            <div>
              <dt>原服务费用</dt>
              <dd>{money(completed.previousService.serviceFeeFen)}</dd>
            </div>
            <div>
              <dt>原助教提成</dt>
              <dd>
                {showCommissionAmount && completed.commissionFen !== null
                  ? money(completed.commissionFen)
                  : "提成已按规则保存，仅店长/老板可查看"}
              </dd>
            </div>
            <div>
              <dt>接替服务上钟</dt>
              <dd>{dateTime(completed.replacementService.startedAt)}</dd>
            </div>
            <div>
              <dt>接替服务实际营业日</dt>
              <dd>{completed.replacementService.businessDate}</dd>
            </div>
          </dl>
        </output>
      ) : null}
    </section>
  );
}
